import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider, SidebarProvider, FeatureFlagsProvider, FeatureGate } from './components/Navbar';
import Navbar         from './components/Navbar';
import Login          from './pages/Login';
import ResetPassword  from './pages/ResetPassword';
import Dashboard      from './pages/Dashboard';
import Campaigns      from './pages/Campaigns';
import Segments       from './pages/Segments';
import Profiles       from './pages/Profiles';
import Analytics      from './pages/Analytics';
import Heatmap        from './pages/Heatmap';
import ShortLinks     from './pages/ShortLinks';
import TeamManagement from './pages/TeamManagement';
import DataRights     from './pages/DataRights';
import Automation     from './pages/Automation';
import AuditLog       from './pages/AuditLog';
import Settings       from './pages/Settings';
import Attribution    from './pages/Attribution';
import ContentManager from './pages/ContentManager';
import Journeys       from './pages/Journeys';
import Invoice        from './pages/Invoice';
import ChannelSync    from './pages/ChannelSync';

const SIDEBAR_W = 220;

function getTokenExp(token) {
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return payload.exp || null;
  } catch { return null; }
}

function getToken() {
  return (
    localStorage.getItem('martech_token') ||
    localStorage.getItem('token') ||
    sessionStorage.getItem('token') ||
    ''
  );
}

function PrivateRoute({ children }) {
  const token = getToken();
  if (!token) return <Navigate to="/login" replace />;
  const exp = getTokenExp(token);
  if (exp && exp * 1000 < Date.now()) {
    ['martech_token', 'martech_totp_verified', 'token', 'role']
      .forEach(k => localStorage.removeItem(k));
    return <Navigate to="/login" state={{ expired: true }} replace />;
  }
  return children;
}

function RootRedirect() {
  return getToken()
    ? <Navigate to="/dashboard" replace />
    : <Navigate to="/login" replace />;
}

function AppLayout() {
  const { pathname } = useLocation();
  const isPublic = pathname === '/login' || pathname === '/' || pathname === '/reset-password';

  return (
    <div style={{ display: 'flex', minHeight: '100vh' }}>
      {!isPublic && <Navbar />}

      <div
        className={isPublic ? '' : 'mts-page-offset'}
        style={{
          flex: 1,
          marginLeft: isPublic ? 0 : SIDEBAR_W,
          minHeight: '100vh',
          transition: 'margin-left 0.2s ease',
        }}
      >
        <Routes>
          <Route path="/"            element={<RootRedirect />} />
          <Route path="/login"       element={<Login />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/dashboard"   element={<PrivateRoute><Dashboard /></PrivateRoute>} />
          <Route path="/campaigns"   element={<PrivateRoute><Campaigns /></PrivateRoute>} />
          <Route path="/automation"  element={<PrivateRoute><FeatureGate flagName="automation"><Automation /></FeatureGate></PrivateRoute>} />
          <Route path="/segments"    element={<PrivateRoute><Segments /></PrivateRoute>} />
          <Route path="/profiles"    element={<PrivateRoute><Profiles /></PrivateRoute>} />
          <Route path="/analytics"   element={<PrivateRoute><Analytics /></PrivateRoute>} />
          <Route path="/heatmap"     element={<PrivateRoute><Heatmap /></PrivateRoute>} />
          <Route path="/short-links" element={<PrivateRoute><FeatureGate flagName="short_links"><ShortLinks /></FeatureGate></PrivateRoute>} />
          <Route path="/data-rights" element={<PrivateRoute><DataRights /></PrivateRoute>} />
          <Route path="/team"        element={<PrivateRoute><TeamManagement /></PrivateRoute>} />
          <Route path="/audit-log"   element={<PrivateRoute><AuditLog /></PrivateRoute>} />
          <Route path="/settings"    element={<PrivateRoute><Settings /></PrivateRoute>} />
          <Route path="/attribution" element={<PrivateRoute><FeatureGate flagName="attribution"><Attribution /></FeatureGate></PrivateRoute>} />
          <Route path="/content"     element={<PrivateRoute><FeatureGate flagName="content_manager"><ContentManager /></FeatureGate></PrivateRoute>} />
          <Route path="/journeys"    element={<PrivateRoute><FeatureGate flagName="journeys"><Journeys /></FeatureGate></PrivateRoute>} />
          <Route path="/invoice"     element={<PrivateRoute><Invoice /></PrivateRoute>} />
          <Route path="/channel-sync" element={<PrivateRoute><FeatureGate flagName="channel_sync"><ChannelSync /></FeatureGate></PrivateRoute>} />
          <Route path="*"            element={<Navigate to="/" replace />} />
        </Routes>
      </div>
    </div>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <SidebarProvider>
        <FeatureFlagsProvider>
          <BrowserRouter>
            <AppLayout />
          </BrowserRouter>
        </FeatureFlagsProvider>
      </SidebarProvider>
    </ThemeProvider>
  );
}
